import {z} from "zod";

/**
 * New-style permission strings
 */
export const DocumentPermission = z.enum(["none", "edit", "view", "comment", "withdrawn"]);
export type DocumentPermission = z.infer<typeof DocumentPermission>;

/**
 * The resolved effective permission level for a user on a resource.
 *
 * Unlike {@link DocumentPermission}, this excludes `"withdrawn"` — that is a
 * persistence concern, not a resolved permission level.  Unlike the legacy
 * numeric {@link PermissionType} enum, this uses human-readable strings that
 * are safe to pass through JSON, log, and compare without importing an enum.
 *
 * Ordered from least to most privileged: none < view < comment < edit.
 */
export type Permission = "none" | "view" | "comment" | "edit";

/**
 * This is the legacy enum for integer values that represent permission levels
 *
 * Wherever possible, lets switch to using strings in the future, because they allow us to add new levels without
 * breaking existing code, and discourage relying on the numeric values here
 *
 * Soft-deprecated in favor of DocumentPermission
 */
export enum PermissionType {
    none = 0,
    edit = 1,
    view = 2,
    comment = 3,
}

// eslint-disable-next-line no-restricted-syntax
export function isPermissionType(value: unknown): value is PermissionType {
    return (typeof value === "string" || typeof value === "number") && value in PermissionType;
}

/**
 * Converts a new-style permission to an old-style permission type integer.
 *
 * Pure isomorphism between the two representations; lives in schema because it
 * is consumed by the {@link DocumentPermissionsData} zod transform.
 */
// eslint-disable-next-line no-restricted-syntax
export function documentPermissionToPermissionType(permission: DocumentPermission): PermissionType {
    switch (permission) {
        case DocumentPermission.Values.none:
        case DocumentPermission.Values.withdrawn:
            return PermissionType.none;
        case DocumentPermission.Values.edit:
            return PermissionType.edit;
        case DocumentPermission.Values.view:
            return PermissionType.view;
        case DocumentPermission.Values.comment:
            return PermissionType.comment;
    }
}

/**
 * Converts an old-style permission type integer to a new-style document permission string.
 *
 * Pure isomorphism between the two representations; lives in schema because it
 * is consumed by the {@link DocumentPermissionsData} zod transform.
 */
// eslint-disable-next-line no-restricted-syntax
export function permissionTypeToDocumentPermission(permissionType: PermissionType): DocumentPermission {
    switch (permissionType) {
        case PermissionType.none:
            return DocumentPermission.Values.none;
        case PermissionType.edit:
            return DocumentPermission.Values.edit;
        case PermissionType.view:
            return DocumentPermission.Values.view;
        case PermissionType.comment:
            return DocumentPermission.Values.comment;
    }
}

/**
 * Converts a legacy numeric {@link PermissionType} to the textual
 * {@link Permission} representation.
 */
// eslint-disable-next-line no-restricted-syntax
export function permissionTypeToPermission(permissionType: PermissionType): Permission {
    switch (permissionType) {
        case PermissionType.none:
            return "none";
        case PermissionType.edit:
            return "edit";
        case PermissionType.view:
            return "view";
        case PermissionType.comment:
            return "comment";
    }
}

/**
 * Converts a textual {@link Permission} to the legacy numeric
 * {@link PermissionType}.
 */
// eslint-disable-next-line no-restricted-syntax
export function permissionToPermissionType(permission: Permission): PermissionType {
    switch (permission) {
        case "none":
            return PermissionType.none;
        case "edit":
            return PermissionType.edit;
        case "view":
            return PermissionType.view;
        case "comment":
            return PermissionType.comment;
    }
}
